import { useCallback } from "react";
import React, { useEffect, useState } from "react";
import { GetBillingCalculationRes, SubscriptionDto } from "../../../../interfaces";
import { DiscountType, SubscriptionStatus } from "../../../../Data/SD";
import { centToUSD } from "../../../../utilities/common/commonFuntion";
import EditDiscountModal from "./EditDiscountModal";
import { useAbortManager } from "../../../../hooks/cancelletionManagerHook/useAbortManager";
import { useAuth } from "../../../../firebase/AuthProvider";
import { GetBillingCalculationByOrgAsync } from "../../../../services/PaymentServices/SubscriptionService";
import { getDisplayErrorMessage } from "../../../../utilities/ErrorMessages/getErrorMessage";
import { Spinner } from "react-bootstrap";
import { Info } from "lucide-react";

interface BillingCalculationProps {
    subscription: SubscriptionDto;
    isSuperAdmin: boolean | undefined;
    orgId: number;
    refreshSubscription: () => void;
}

const BillingCalculation: React.FC<BillingCalculationProps> = ({ subscription, isSuperAdmin = false, orgId, refreshSubscription }) => {
    const cancelletionManager = useAbortManager();
    const { currentUser } = useAuth();
    const [showEditDiscountModal, setShowEditDiscountModal] = useState(false);

    const [calculationData, setCalculationData] = useState<GetBillingCalculationRes>();
    const [loading, setLoading] = useState(false);
    const [reload, ] = useState(false);

    const getBillingCalculation = useCallback(async (showLoading: boolean = true) => {
        try {
            if (showLoading) setLoading(true);
            if (!subscription.plan) return;
            const token = await currentUser?.getIdToken();
            if (!token) return;

            const controller = cancelletionManager.create("GetBillingCalculation");
            var res = await GetBillingCalculationByOrgAsync(token, orgId, controller.signal);
            if (res.success) {
                setCalculationData(res.data);
            }
        } catch (error) {
            const msg = getDisplayErrorMessage(error, "Error to get the data");
            console.error(msg);
        } finally {
            if (showLoading) setLoading(false);
        }


    }, [currentUser, orgId, subscription.plan, cancelletionManager])

useEffect(() => {
        getBillingCalculation();
    }, [reload, getBillingCalculation]);

    const handleEditDiscount = () => {
        setShowEditDiscountModal(true);
    };



    const startSubscriptionBtnEnable =
        subscription?.status === SubscriptionStatus.TRIAL_ACTIVE.toString() ||
        subscription?.status === SubscriptionStatus.READ_ONLY_TRIAL_EXPIRED.toString();


    // const applicableDiscountCents = calculationData?.subTotalCents && calculateDiscount(
    //     calculationData?.subTotalCents,
    //     subscription.discountType as DiscountType,
    //     subscription.discountValue
    // ) || 0;

    // const referralDiscountCents =  calculationData?.referalDiscountValue && calculateDiscount(
    //     calculationData.costOfAllUsersCents - applicableDiscountCents,
    //     DiscountType.PERCENT,
    //     calculationData.referalDiscountValue
    // );

    // console.log("REfreralDiscountCents : ", referralDiscountCents);
    // console.log("calculationData?.referalDiscountValue : ", calculationData?.referalDiscountValue);
    // console.log("calculationData.referalDiscountType : ", calculationData?.referalDiscountType);

    const creditAppliedCents = subscription.canUseCreditBalance ? Math.min(calculationData?.estimatedTotalCents || 0, subscription.creditBalanceCents) : 0;

    const showApplicableDiscount = (discountType: string | null | undefined, value: number): string => {
        if (discountType && discountType === DiscountType.PERCENT.toString()) {
            return value + " %"
        } else {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
            }).format(value);
        }
    }

    return (
        <>
            <div className="bc_container">
                <div className="bc_header">
                    <h3 className="bc_title">Billing Calculation</h3>

                    {isSuperAdmin && (
                        <button className="bc_edit_btn" onClick={handleEditDiscount}>
                            Edit Discount
                        </button>
                    )}
                </div>

                {startSubscriptionBtnEnable ? (
                    <span className="d-flex justify-content-center align-items-center m-5">
                        <button>Start Subscription</button>
                    </span>
                ) : (
                    <div>
                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center">
                                <Spinner size="sm" />
                            </div>
                        ) : (
                            <>
                                <div className="bc_row">
                                    <span>Cost Per User</span>
                                    <span>{calculationData?.seatUnitPriceCents && centToUSD(calculationData?.seatUnitPriceCents)}</span>
                                </div>

                                <div className="bc_row">
                                    <span>Base Usage Cost</span>
                                    <span>
                                        {calculationData?.baseUsesCostCents
                                            ? centToUSD(calculationData?.baseUsesCostCents)
                                            : "NA"}
                                    </span>
                                </div>

                                <div className="bc_row">
                                    <span>Monthly Usage Total</span>
                                    <span>{calculationData?.costOfAllUsersCents && centToUSD(calculationData?.costOfAllUsersCents)}</span>
                                </div>

                                <div className="bc_divider" />

                                <div className="bc_row">
                                    <span>SubTotal (Max of Base or Usage)</span>
                                    <span>{calculationData?.subTotalCents && centToUSD(calculationData?.subTotalCents)}</span>
                                </div>

                                <div className="bc_row">
                                    <span>Discount Type</span>
                                    <span>{subscription.orgDiscount?.discountType}</span>
                                </div>

                                <div className="bc_row">
                                    <span>Discount Value</span>
                                    <span>{showApplicableDiscount(subscription.orgDiscount?.discountType, subscription.orgDiscount?.discountValue || 0)}</span>
                                </div>

                                <div className="bc_row">
                                    <span>Applicable Discount</span>
                                    <span>- {centToUSD(calculationData?.normalDiscountAppliedCents)}</span>
                                </div>

                                {
                                    calculationData?.referalDiscountValue && calculationData?.referalDiscountAppliedCents ? (
                                        <>
                                            <div className="bc_row">
                                                <span>Referral Discount Value</span>
                                                <span>{calculationData.referalDiscountValue}%</span>
                                            </div>

                                            <div className="bc_row">
                                                <span>Applicable Referral Discount</span>
                                                <span>- {centToUSD(calculationData?.referalDiscountAppliedCents)}</span>
                                            </div>
                                        </>
                                    ) : <></>
                                }

                                {
                                    subscription.canUseCreditBalance ? (
                                        <>
                                            <div className="bc_row">
                                                <span>Applicable Credit Used for billing</span>
                                                <span>{centToUSD(creditAppliedCents)}</span>
                                            </div>
                                        </>
                                    ) : <></>
                                }

                                <div className="bc_divider" />

                                <div className="bc_total">
                                    <span>Estimated Total</span>
                                    <span className="bc_total_price">
                                        {centToUSD((calculationData?.estimatedTotalCents || 0) - creditAppliedCents)}
                                    </span>
                                </div>

                                <div className="bc_info_text">
                                    <Info size={16} className="bc_info_icon" />
                                    <span>
                                        Your payment will be automatically charged on the next billing date.
                                    </span>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {showEditDiscountModal && orgId && (
                <EditDiscountModal
                    isOpen={showEditDiscountModal}
                    onClose={() => setShowEditDiscountModal(false)}
                    onSuccess={() => { getBillingCalculation(false); refreshSubscription(); setShowEditDiscountModal(false); }}
                    subscription={subscription}
                    orgId={orgId}
                />
            )}
        </>
    );
};

export default BillingCalculation;